import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/ui/empty-state';
import { emptyStateIcon } from '@/components/ui/empty-state-icon';
import { Skeleton } from '@/components/ui/skeleton';
import { TooltipProvider } from '@/components/ui/tooltip';
import { useInSettingsPane } from '@/containers/settings/settings-pane';
import { iconMinus, iconPlus, iconTriangleExclamation } from '@/lib/icons';
import { useEffect, useRef, useState } from 'react';
import {
  useAppearance,
  type AppearanceContextValue,
} from './appearance-context';
import {
  applyPreset,
  presetFor,
  type AppearancePreset,
} from './appearance-presets';
import {
  MAX_COLORS,
  wrapHue,
  type AppearanceColor,
  type AppearanceLook,
} from './appearance.util';
import { ColourField } from './colour-field.component';
import { GrainDial } from './grain-dial.component';
import { IconControl } from './icon-control.component';
import { IconStyleSwitch } from './icon-style-switch.component';
import { IntensitySlider } from './intensity-slider.component';
import { ModeSwitch } from './mode-switch.component';
import { PresetSwatches } from './preset-swatches.component';

/** The first colour Add colour gives a look with none: the Blue preset's. */
const FIRST_COLOR: AppearanceColor = { hue: 245, tone: 40 };

/** A new colour sits this far round the hues from the chosen one. */
const HUE_STEP = 60;

const LoadFailedGlyph = emptyStateIcon(iconTriangleExclamation);

function AppearanceLoadFailed({ onRetry }: { onRetry: () => void }) {
  return (
    <div role='alert'>
      <EmptyState
        icon={LoadFailedGlyph}
        headline="Couldn't load your appearance"
        action={
          <Button
            variant='outline'
            size='sm'
            className='h-8 rounded-md text-xs'
            onClick={onRetry}
          >
            Retry
          </Button>
        }
      />
    </div>
  );
}

function AppearanceSkeleton() {
  return (
    <div aria-busy='true' className='flex w-full max-w-[320px] flex-col gap-4'>
      <Skeleton className='h-10 w-full rounded-[10px]' />
      <Skeleton className='h-40 w-full rounded-xl' />
      <Skeleton className='h-8 w-full rounded-lg' />
    </div>
  );
}

function AppearancePanel({
  appearance,
}: {
  appearance: AppearanceContextValue;
}) {
  const { look, preference, preview, commit, reset, setPreference } =
    appearance;
  // In the settings pane the picker sits on the pane; beside the account
  // menu it is its own card.
  const inPane = useInSettingsPane();
  // The look while a control is held: previewed on the chrome, saved on
  // release. The ref has the newest one for a release in the same tick.
  const [draft, setDraft] = useState<AppearanceLook | null>(null);
  const latest = useRef<AppearanceLook | null>(null);
  const shown = draft ?? look;
  const [chosen, setChosen] = useState(0);
  const active = Math.min(chosen, Math.max(0, shown.colors.length - 1));

  useEffect(() => () => preview(null), [preview]);

  const handles = useRef<(HTMLDivElement | null)[]>([]);
  const focusAfter = useRef<number | null>(null);
  useEffect(() => {
    if (focusAfter.current === null) return;
    handles.current[focusAfter.current]?.focus();
    focusAfter.current = null;
  });

  const preset = presetFor(shown);
  // Intensity and grain act on picked colours; with none they would change
  // nothing on screen.
  const noColour = shown.colors.length === 0;

  const change = (next: AppearanceLook) => {
    latest.current = next;
    setDraft(next);
    preview(next);
  };
  const settle = () => {
    const next = latest.current;
    latest.current = null;
    setDraft(null);
    if (next) commit(next);
  };
  const cancel = () => {
    latest.current = null;
    setDraft(null);
    preview(null);
  };
  const save = (next: AppearanceLook) => {
    latest.current = null;
    setDraft(null);
    commit(next);
  };

  const selectPreset = (picked: AppearancePreset) => {
    setChosen(0);
    save(applyPreset(picked, look));
  };

  const addColor = () => {
    if (look.colors.length >= MAX_COLORS) return;
    const from = look.colors[active];
    const color = from
      ? { hue: wrapHue(from.hue + HUE_STEP), tone: from.tone }
      : FIRST_COLOR;
    const index = look.colors.length;
    setChosen(index);
    focusAfter.current = index;
    save({ ...look, colors: [...look.colors, color] });
  };

  /** Drops a colour, the chosen one unless named, and focuses the one before. */
  const removeColor = (removed = active) => {
    if (look.colors.length <= 1) return;
    const index = Math.max(0, removed - 1);
    setChosen(index);
    focusAfter.current = index;
    save({ ...look, colors: look.colors.filter((_, at) => at !== removed) });
  };

  return (
    <TooltipProvider delayDuration={300}>
      <section
        aria-label='Appearance'
        className={
          inPane
            ? 'flex w-full max-w-[320px] flex-col gap-4'
            : 'flex w-full max-w-[320px] flex-col gap-4 rounded-2xl border bg-card p-4 shadow-xs'
        }
      >
        <ModeSwitch value={preference} onChange={setPreference} />
        <IconStyleSwitch />
        <div className='flex flex-col gap-2'>
          <ColourField
            colors={shown.colors}
            intensity={shown.intensity}
            active={active}
            onActiveChange={setChosen}
            onChange={(colors) => change({ ...shown, colors })}
            onCommit={settle}
            onCancel={cancel}
            onRemove={removeColor}
            handleRef={(index, node) => {
              handles.current[index] = node;
            }}
          />
          <div className='flex items-center justify-center gap-2'>
            <IconControl
              label='Remove colour'
              icon={iconMinus}
              disabled={shown.colors.length <= 1}
              onClick={() => removeColor()}
            />
            <IconControl
              label='Add colour'
              icon={iconPlus}
              disabled={shown.colors.length >= MAX_COLORS}
              onClick={addColor}
            />
          </div>
        </div>
        <PresetSwatches
          selectedId={preset?.id ?? null}
          onSelect={selectPreset}
        />
        <div className='flex items-end gap-4'>
          <IntensitySlider
            value={shown.intensity}
            disabled={noColour}
            onChange={(intensity) => change({ ...shown, intensity })}
            onCommit={settle}
            onCancel={cancel}
          />
          <GrainDial
            value={shown.grain}
            disabled={noColour}
            onChange={(grain) => change({ ...shown, grain })}
            onCommit={settle}
            onCancel={cancel}
          />
        </div>
        <div className='flex justify-end'>
          <Button
            variant='outline'
            size='sm'
            disabled={appearance.isDefault}
            onClick={() => {
              setChosen(0);
              reset();
            }}
          >
            Reset
          </Button>
        </div>
      </section>
    </TooltipProvider>
  );
}

/**
 * The appearance picker: mode, icon style, the colour field, add and remove
 * colour, presets, intensity and grain. Settings mounts it as its Appearance item.
 * Renders nothing outside the appearance engine, a skeleton while the saved
 * appearance loads, and Retry when it cannot be read.
 */
export function AppearanceSettings() {
  const appearance = useAppearance();
  if (!appearance) return null;
  // The saved appearance is on its way: the picker's column, as a skeleton,
  // so the page does not open on an empty pane. If it cannot be read, Retry.
  if (!appearance.ready)
    return appearance.failed ? (
      <AppearanceLoadFailed onRetry={appearance.retry} />
    ) : (
      <AppearanceSkeleton />
    );
  return <AppearancePanel appearance={appearance} />;
}
